import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { PageHeader } from "./DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { Progress } from "../components/ui/progress";
import { Checkbox } from "../components/ui/checkbox";
import { Crosshair, Loader2, ScanLine, Phone, ExternalLink, Play, Gavel, X, Zap } from "lucide-react";
import { toast } from "sonner";
import GeoMap from "../components/GeoMap";

function statusBadge(s) {
  if (s === "COMPLETED") return "bg-emerald-600 text-white";
  if (s === "QUEUED") return "bg-amber-500 text-zinc-950";
  return "bg-zinc-200 text-zinc-800";
}

export default function GeoBlitz() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [zip, setZip] = useState(searchParams.get("zip") || "");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [radius, setRadius] = useState([2]);
  // Two-mode category selector. Short-list pick wins; otherwise free-text used.
  const [shortlistCategories, setShortlistCategories] = useState([]);
  const [categoryPick, setCategoryPick] = useState(""); // shortlist value or ""
  const [categoryFree, setCategoryFree] = useState("");
  const [deepScan, setDeepScan] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scan, setScan] = useState(null);
  const [leads, setLeads] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [runningRowId, setRunningRowId] = useState(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  // Overlay-vendor filter: show all leads OR only those with an overlay
  // widget detected (highest-value pitch targets).
  const [overlayFilter, setOverlayFilter] = useState("all"); // "all" | "overlay_only"
  const pollRef = useRef(null);
  const [scans, setScans] = useState([]);
  const [focusLeadId, setFocusLeadId] = useState(null);
  // Sticky pitch-context lawsuit: when present, every audit triggered from
  // this scan inherits the lawsuit and its PDF dossier gets the neighbor-
  // warning banner.
  const [lawsuitContext, setLawsuitContext] = useState(null);

  // When the URL ?zip= changes (e.g., bridged from Lawsuit Radar), prefill input
  useEffect(() => {
    const qz = searchParams.get("zip");
    if (qz && qz !== zip) {
      setZip(qz);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Fetch lawsuit details when ?lawsuit=<id> is present and stick it in state.
  useEffect(() => {
    const lid = searchParams.get("lawsuit");
    if (!lid) {
      setLawsuitContext(null);
      return;
    }
    (async () => {
      try {
        const r = await api.get(`/lawsuits/${lid}`);
        setLawsuitContext(r.data);
        toast.success(
          `Pitch context loaded: ${r.data.business_name} v. ${r.data.plaintiff_law_firm}`,
          { duration: 5000 },
        );
      } catch {
        toast.warning("Could not load lawsuit context — banner will not be attached");
      }
    })();
  }, [searchParams]);

  const clearLawsuitContext = () => {
    setLawsuitContext(null);
    const sp = new URLSearchParams(searchParams);
    sp.delete("lawsuit");
    setSearchParams(sp, { replace: true });
  };

  // Load curated short-list of ADA-lawsuit-magnet niches once.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/v1/geo/categories");
        setShortlistCategories(r.data?.shortlist || []);
      } catch {
        /* non-blocking */
      }
    })();
  }, []);

  const loadScans = async () => {
    try {
      const r = await api.get("/geo/scans");
      setScans(r.data);
    } catch {}
  };
  useEffect(() => { loadScans(); }, []);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const startScan = async (e) => {
    e?.preventDefault();
    if (!zip && !(lat && lng)) {
      toast.error("Provide ZIP/address or lat & lng");
      return;
    }
    setScanning(true);
    setLeads([]);
    setScan(null);
    setSelectedIds(new Set());
    try {
      const body = { radius_miles: Math.max(1, Math.min(radius[0], 25)) };
      if (zip) body.zip_code = zip;
      if (lat && lng) {
        body.latitude = parseFloat(lat);
        body.longitude = parseFloat(lng);
      }
      // Two-mode category: short-list dropdown takes priority over free text.
      const cat = (categoryPick || categoryFree || "").trim();
      if (cat) body.business_category = cat;
      if (deepScan) body.deep_scan = true;
      if (lawsuitContext?.id) body.pitch_context_lawsuit_id = lawsuitContext.id;
      const r = await api.post("/geo/scan", body);
      toast.success(`Discovered ${r.data.total_found} local businesses with websites`);
      await fetchScan(r.data.scan_id);
      await loadScans();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const fetchScan = async (scanId) => {
    const r = await api.get(`/geo/scans/${scanId}`);
    setScan(r.data.scan);
    setLeads(r.data.leads);
    // Stage-4: if we land on a scan that still has in-flight audits
    // (page refresh mid-batch), resume polling automatically so the UI
    // doesn't stall.
    const inflight = (r.data.leads || []).some((l) => l.audit_status === "QUEUED" || l.audit_status === "RUNNING");
    if (inflight) ensurePolling();
  };

  // Polling helper — starts an interval that refreshes the scan until no queued leads remain
  const ensurePolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      if (!scan) return;
      try {
        const r = await api.get(`/geo/scans/${scan.id}`);
        setScan(r.data.scan);
        setLeads(r.data.leads);
        const stillQueued = r.data.leads.filter((l) => l.audit_status === "QUEUED").length;
        if (stillQueued === 0) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setRunningRowId(null);
          setBulkRunning(false);
        }
      } catch {}
    }, 2000);
  };

  const auditOne = async (lead) => {
    setRunningRowId(lead.id);
    try {
      await api.post(`/geo/leads/${lead.id}/audit`);
      toast.success(`Audit queued for ${lead.business_name}`);
      // optimistic update
      setLeads((xs) => xs.map((l) => (l.id === lead.id ? { ...l, audit_status: "QUEUED" } : l)));
      ensurePolling();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to queue audit");
      setRunningRowId(null);
    }
  };

  const auditSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkRunning(true);
    try {
      const r = await api.post(`/geo/leads/audit-bulk`, { lead_ids: ids });
      toast.success(`Bulk audit queued · ${r.data.queued} site${r.data.queued === 1 ? "" : "s"}`);
      setLeads((xs) =>
        xs.map((l) => (selectedIds.has(l.id) ? { ...l, audit_status: "QUEUED" } : l))
      );
      setSelectedIds(new Set());
      ensurePolling();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk audit failed");
      setBulkRunning(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Stage-4: compute selectable / visible IDs once per render for consistent
  // selection semantics (header checkbox, action button count, "select all
  // N matching", "clear selection").
  const norm = (v) => (v || "").toLowerCase();
  const bucketFor = (v) => {
    const n = norm(v);
    if (!n) return "";
    if (n.includes("accessibe")) return "accessibe";
    if (n.includes("userway")) return "userway";
    if (n.includes("equalweb")) return "equalweb";
    if (n.includes("audioeye")) return "audioeye";
    return "other";
  };
  const applyFilter = (rows) => {
    if (overlayFilter === "pending") return rows.filter((l) => l.overlay_probed_at == null && l.overlay_probe_status !== "failed");
    if (overlayFilter === "detected") return rows.filter((l) => !!l.overlay_vendor);
    if (overlayFilter === "not_detected") return rows.filter((l) => l.overlay_probed_at != null && !l.overlay_vendor);
    if (overlayFilter === "failed") return rows.filter((l) => l.overlay_probe_status === "failed");
    if (["accessibe","userway","equalweb","audioeye","other_vendor"].includes(overlayFilter)) {
      const wanted = overlayFilter === "other_vendor" ? "other" : overlayFilter;
      return rows.filter((l) => bucketFor(l.overlay_vendor) === wanted);
    }
    if (overlayFilter === "overlay_only") return rows.filter((l) => !!l.overlay_vendor);
    return rows;
  };
  const visibleLeads = applyFilter(leads);
  const isEligible = (l) => l.website_url && l.audit_status !== "QUEUED" && l.audit_status !== "COMPLETED" && l.audit_status !== "RUNNING";
  const visibleSelectableIds = visibleLeads.filter(isEligible).map((l) => l.id);
  const ineligibleVisibleCount = visibleLeads.length - visibleSelectableIds.length;
  const selectedVisibleIds = visibleSelectableIds.filter((id) => selectedIds.has(id));
  const allVisibleEligibleSelected = visibleSelectableIds.length > 0 && selectedVisibleIds.length === visibleSelectableIds.length;
  const someVisibleSelected = selectedVisibleIds.length > 0 && !allVisibleEligibleSelected;

  const toggleSelectAll = () => {
    // Header checkbox = select-all-eligible on the visible (filtered) set.
    setSelectedIds((prev) => {
      if (allVisibleEligibleSelected) {
        // deselect only the visible set, keep other selections intact
        const next = new Set(prev);
        visibleSelectableIds.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      visibleSelectableIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  // Stage-4: preserve selections while paging (no pagination here today,
  // but we keep them across scroll). Clear when filter changes so the
  // action button count never lies about the visible set.
  useEffect(() => {
    setSelectedIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayFilter]);

  // Stage-2 batch progress: always calculate against the number of leads
  // actually queued (`scan.batch_total`), NEVER against the discovered
  // total. If no batch is in flight yet, fall back to the lead rollup.
  const scanBatch = scan || {};
  const batchTotal = scanBatch.batch_total || 0;
  const batchQueued = scanBatch.batch_queued || 0;
  const batchRunning = scanBatch.batch_running || 0;
  const batchCompleted = scanBatch.batch_completed || 0;
  const batchFailed = scanBatch.batch_failed || 0;
  const batchSkipped = scanBatch.batch_skipped || 0;
  const batchFinished = batchCompleted + batchFailed;
  const pct = batchTotal ? Math.round((batchFinished / batchTotal) * 100) : 0;
  // Discovery-side rollup (independent of batch state).
  const discovered = leads.length;
  const completed = leads.filter((l) => l.audit_status === "COMPLETED").length;
  const queued = leads.filter((l) => l.audit_status === "QUEUED").length;
  const total = discovered;

  return (
    <div data-testid="geo-blitz-page">
      <PageHeader
        eyebrow="Module 02 · Geo-Blitz Console"
        title="Proximity scanner & bulk audit queue"
        description="Drop a ZIP code or coordinate pair and a 1–25 mile radius. The console auto-discovers local brick-and-mortar businesses with websites and queues them for background compliance auditing."
        testid="geo-blitz-header"
      />

      {lawsuitContext && (
        <div
          className="max-w-[1600px] mx-auto px-4 md:px-8 -mt-2 mb-2"
          data-testid="lawsuit-context-banner"
        >
          <div className="border-2 border-red-700 bg-red-50 px-4 py-3 flex items-start gap-3">
            <Gavel className="w-5 h-5 text-red-700 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-display text-sm font-black uppercase tracking-wider text-red-900 leading-tight">
                Pitch context active · neighbor warning will appear on every PDF
              </div>
              <div className="text-xs text-red-900 mt-1 font-mono break-words">
                <span className="font-bold">{lawsuitContext.business_name}</span>
                <span className="text-red-700"> v. </span>
                <span className="font-bold">{lawsuitContext.plaintiff_law_firm}</span>
                {lawsuitContext.cause_of_action && (
                  <span className="text-red-700"> · {lawsuitContext.cause_of_action}</span>
                )}
                {lawsuitContext.case_number && (
                  <span className="text-red-600 ml-2">[{lawsuitContext.case_number}]</span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={clearLawsuitContext}
              className="shrink-0 inline-flex items-center gap-1 px-2 py-1 border border-red-700 text-red-800 hover:bg-red-700 hover:text-white text-[10px] font-bold uppercase tracking-wider transition-colors"
              data-testid="lawsuit-context-clear"
              title="Clear context — subsequent audits won't include the warning"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          </div>
        </div>
      )}

      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-6 grid grid-cols-1 lg:grid-cols-[420px_minmax(0,1fr)] gap-6">
        {/* Left: control panel */}
        <div>
          <form onSubmit={startScan} className="border border-zinc-950 p-5 bg-white" data-testid="geo-blitz-form">
            <div className="label-xs mb-1">Search target</div>
            <Label className="text-xs font-bold">ZIP code or address</Label>
            <Input
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              placeholder="e.g. 10001 or 'Soho, NYC'"
              className="rounded-sm h-11 mt-1 border-zinc-300 font-mono"
              data-testid="geo-zip-input"
            />

            <div className="mt-3 grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] font-bold uppercase">Latitude</Label>
                <Input value={lat} onChange={(e) => setLat(e.target.value)} placeholder="40.7128" className="rounded-sm h-10 mt-1 font-mono text-xs" data-testid="geo-lat-input" />
              </div>
              <div>
                <Label className="text-[10px] font-bold uppercase">Longitude</Label>
                <Input value={lng} onChange={(e) => setLng(e.target.value)} placeholder="-74.0060" className="rounded-sm h-10 mt-1 font-mono text-xs" data-testid="geo-lng-input" />
              </div>
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold uppercase tracking-wider">Radius</Label>
                <span className="font-mono text-sm font-bold">{radius[0]} mi</span>
              </div>
              <Slider
                value={radius}
                min={1}
                max={25}
                step={1}
                onValueChange={setRadius}
                className="mt-2"
                data-testid="geo-radius-slider"
              />
              <div className="flex justify-between text-[10px] text-zinc-500 font-mono mt-1">
                <span>1 mi</span><span>25 mi</span>
              </div>
            </div>

            <div className="mt-4 border-t border-zinc-200 pt-3" data-testid="geo-category-picker">
              <Label className="text-xs font-bold uppercase tracking-wider">
                Business type <span className="text-zinc-400 font-normal">(optional)</span>
              </Label>
              <div className="mt-1">
                <select
                  value={categoryPick}
                  onChange={(e) => {
                    setCategoryPick(e.target.value);
                    if (e.target.value) setCategoryFree("");
                  }}
                  className="w-full h-10 rounded-sm border border-zinc-950 bg-white px-2 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-blue-600"
                  data-testid="geo-category-shortlist"
                >
                  <option value="">— Choose a high-conversion niche —</option>
                  {shortlistCategories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-[10px] text-zinc-500 font-mono uppercase">or type</span>
                <Input
                  value={categoryFree}
                  onChange={(e) => {
                    setCategoryFree(e.target.value);
                    if (e.target.value) setCategoryPick("");
                  }}
                  placeholder="e.g. electrician, tattoo studio, tax preparer"
                  className="rounded-sm h-10 font-mono text-xs flex-1"
                  data-testid="geo-category-freetext"
                  disabled={!!categoryPick}
                />
              </div>
              <p className="mt-1.5 text-[10px] text-zinc-500 font-mono leading-snug">
                {categoryPick
                  ? `Will run a precision Google Places search for "${categoryPick}".`
                  : categoryFree
                  ? `Will run a Google Places free-text search for "${categoryFree}".`
                  : "No filter → broad scan of common SMB types."}
              </p>
            </div>

            <div
              className={`mt-4 border-2 ${deepScan ? "border-blue-700 bg-blue-50/60" : "border-zinc-300"} p-3 transition-colors`}
              data-testid="geo-deepscan-panel"
            >
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox
                  checked={deepScan}
                  onCheckedChange={(v) => setDeepScan(!!v)}
                  className="mt-0.5"
                  data-testid="geo-deepscan-toggle"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Zap className={`w-3.5 h-3.5 ${deepScan ? "text-blue-700" : "text-zinc-400"}`} />
                    <span className={`text-xs font-bold uppercase tracking-wider ${deepScan ? "text-blue-900" : "text-zinc-700"}`}>
                      Deep scan (4× quadrants)
                    </span>
                  </div>
                  <p className="mt-1 text-[10px] text-zinc-600 font-mono leading-snug">
                    Splits the circle into 5 overlapping sub-scans (center + NE/NW/SE/SW) to beat
                    Google's 20-per-call cap. Typically 3–4× more SMB leads, ~5× the API quota.
                  </p>
                </div>
              </label>
            </div>

            <Button
              type="submit"
              disabled={scanning}
              className="w-full mt-5 h-11 rounded-sm bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wider"
              data-testid="geo-blitz-start-button"
            >
              {scanning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning OSM…</> : <><Crosshair className="w-4 h-4 mr-2" /> Run Geo-Blitz</>}
            </Button>

            {scan && (
              <div className="mt-5 pt-4 border-t border-zinc-200">
                <div className="label-xs mb-1">Last scan</div>
                <div className="text-xs font-mono text-zinc-700 mb-2 break-all">{scan.center_label}</div>

                <div className="mt-2" role="status" aria-live="polite" aria-atomic="true">
                  <div className="flex justify-between text-xs font-mono mb-1">
                    <span>Batch progress</span>
                    <span data-testid="geo-batch-progress-text">
                      {batchFinished}/{batchTotal || 0} · {pct}%
                    </span>
                  </div>
                  <Progress value={pct} className="h-2 rounded-none" data-testid="geo-audit-progress" aria-label={`Batch audit progress ${pct} percent`} />
                  <div className="flex gap-3 mt-2 text-[10px] font-bold uppercase tracking-wider text-zinc-500 flex-wrap">
                    <span><span className="inline-block w-2 h-2 bg-zinc-400 mr-1" />Queued {batchQueued}</span>
                    <span><span className="inline-block w-2 h-2 bg-blue-500 mr-1" />Running {batchRunning}</span>
                    <span><span className="inline-block w-2 h-2 bg-emerald-600 mr-1" />Completed {batchCompleted}</span>
                    <span><span className="inline-block w-2 h-2 bg-red-500 mr-1" />Failed {batchFailed}</span>
                    <span><span className="inline-block w-2 h-2 bg-zinc-300 mr-1" />Skipped {batchSkipped}</span>
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-1">
                    Discovery: {discovered} businesses found (progress reflects only the {batchTotal} you queued).
                  </div>
                </div>

                <p className="mt-4 text-[11px] text-zinc-500 leading-snug">
                  Use the checkboxes on the leads table to pick targets, then click
                  <strong className="text-zinc-700"> "Run Audit on Selected"</strong> — or hit the inline
                  <strong className="text-zinc-700"> ▶ Audit</strong> button on any single row.
                </p>
              </div>
            )}
          </form>

          {/* Past scans */}
          {scans.length > 0 && (
            <div className="mt-4 border border-zinc-200 p-4">
              <div className="label-xs mb-2">Recent scans</div>
              <ul className="space-y-1" aria-label="Recent Geo-Blitz scans">
                {scans.slice(0, 5).map((s) => (
                  <li key={s.id}>
                    <button
                      onClick={() => fetchScan(s.id)}
                      className="text-left text-xs hover:bg-zinc-100 px-2 py-1 w-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid={`recent-scan-${s.id}`}
                      aria-label={`Load scan ${s.center_label}, ${s.total_found} businesses found`}
                    >
                      <div className="font-mono truncate">{s.center_label}</div>
                      <div className="text-[10px] text-zinc-500 flex flex-wrap gap-x-2">
                        <span>{s.total_found} found · r={s.radius_miles}mi</span>
                        {s.created_at && (
                          <span data-testid={`recent-scan-time-${s.id}`}>
                            · {new Date(s.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Right: map + leads */}
        <div>
          {/* Real Leaflet map — OSM tiles, radius circle, color-coded pins */}
          <GeoMap
            centerLat={scan?.center_lat}
            centerLng={scan?.center_lng}
            radiusMiles={scan?.radius_miles ?? radius[0]}
            leads={leads}
            focusId={focusLeadId}
            onPinClick={(id) => setFocusLeadId(id)}
            onAuditPin={(lead) => auditOne(lead)}
          />

          {/* Leads table */}
          <div className="border border-zinc-950">
            {/* Header row 1 · title + vendor filter chips */}
            <div className="bg-zinc-950 text-white px-4 py-2 flex items-center gap-3 flex-wrap">
              <div className="label-xs text-zinc-400 shrink-0">Discovered businesses</div>
              {/* Overlay-vendor filter — Stage-2: vendor-specific chips
                  with distinct pending / detected / not_detected / failed
                  states, plus per-vendor toggles for AccessiBe / UserWay /
                  equalweb / AudioEye / other. */}
              <div className="flex items-center gap-1 border border-zinc-700 flex-wrap min-w-0" role="group" aria-label="Overlay vendor filter">
                {(() => {
                  const counts = {
                    pending: leads.filter((l) => (l.overlay_probe_status || (l.overlay_probed_at == null ? "pending" : "not_detected")) === "pending").length,
                    detected: leads.filter((l) => !!l.overlay_vendor).length,
                    not_detected: leads.filter((l) => l.overlay_probed_at != null && !l.overlay_vendor).length,
                    failed: leads.filter((l) => (l.overlay_probe_status === "failed")).length,
                    accessibe: leads.filter((l) => bucketFor(l.overlay_vendor) === "accessibe").length,
                    userway: leads.filter((l) => bucketFor(l.overlay_vendor) === "userway").length,
                    equalweb: leads.filter((l) => bucketFor(l.overlay_vendor) === "equalweb").length,
                    audioeye: leads.filter((l) => bucketFor(l.overlay_vendor) === "audioeye").length,
                    other_vendor: leads.filter((l) => bucketFor(l.overlay_vendor) === "other").length,
                  };
                  const chip = (id, label, tone) => (
                    <button
                      type="button"
                      onClick={() => setOverlayFilter(id)}
                      aria-pressed={overlayFilter === id}
                      className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider focus:outline-none focus:ring-2 focus:ring-blue-500 ${overlayFilter === id ? tone : "text-zinc-300 hover:text-white"}`}
                      data-testid={`overlay-filter-${id}`}
                    >
                      {label}
                    </button>
                  );
                  return (
                    <>
                      {chip("all", `All (${leads.length})`, "bg-white text-zinc-950")}
                      {chip("pending", `Pending (${counts.pending})`, "bg-zinc-500 text-white")}
                      {chip("detected", `Detected (${counts.detected})`, "bg-amber-400 text-zinc-950")}
                      {chip("not_detected", `Not detected (${counts.not_detected})`, "bg-emerald-200 text-zinc-950")}
                      {chip("failed", `Failed (${counts.failed})`, "bg-red-400 text-zinc-950")}
                      {counts.accessibe > 0 && chip("accessibe", `AccessiBe (${counts.accessibe})`, "bg-amber-400 text-zinc-950")}
                      {counts.userway > 0 && chip("userway", `UserWay (${counts.userway})`, "bg-amber-400 text-zinc-950")}
                      {counts.equalweb > 0 && chip("equalweb", `Equalweb (${counts.equalweb})`, "bg-amber-400 text-zinc-950")}
                      {counts.audioeye > 0 && chip("audioeye", `AudioEye (${counts.audioeye})`, "bg-amber-400 text-zinc-950")}
                      {counts.other_vendor > 0 && chip("other_vendor", `Other vendor (${counts.other_vendor})`, "bg-amber-400 text-zinc-950")}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Header row 2 · explicit selection semantics + primary action */}
            <div
              className="bg-zinc-900 text-white px-4 py-2 flex items-center gap-3 flex-wrap"
              data-testid="geo-selection-toolbar"
              role="region"
              aria-label="Selection summary"
            >
              <div className="font-mono text-xs text-zinc-200" data-testid="geo-filter-summary">
                <span data-testid="geo-shown-count">{visibleLeads.length}</span> shown of{" "}
                <span data-testid="geo-total-count">{leads.length}</span> total ·{" "}
                <span className="text-white font-bold" data-testid="geo-selected-count">{selectedIds.size}</span> selected
                {ineligibleVisibleCount > 0 && (
                  <span className="ml-2 text-[10px] text-zinc-400" title="Rows without a website or already queued/completed can't be re-audited from this batch">
                    ({ineligibleVisibleCount} not selectable — no website or already audited)
                  </span>
                )}
              </div>
              {someVisibleSelected && visibleSelectableIds.length > selectedVisibleIds.length && (
                <button
                  type="button"
                  onClick={() => setSelectedIds((prev) => {
                    const next = new Set(prev);
                    visibleSelectableIds.forEach((id) => next.add(id));
                    return next;
                  })}
                  className="text-[10px] font-bold uppercase tracking-wider text-blue-300 underline hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="geo-select-all-matching"
                >
                  Select all {visibleSelectableIds.length} matching results
                </button>
              )}
              {selectedIds.size > 0 && (
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-[10px] font-bold uppercase tracking-wider text-zinc-300 underline hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="geo-clear-selection"
                >
                  Clear selection
                </button>
              )}
              <div className="ml-auto">
                <Button
                  type="button"
                  onClick={auditSelected}
                  disabled={selectedIds.size === 0 || bulkRunning}
                  className="rounded-sm bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wider text-xs h-8 px-3 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="geo-audit-selected-button"
                  aria-label={`Run audits on ${selectedIds.size} selected site${selectedIds.size === 1 ? "" : "s"}`}
                >
                  {bulkRunning ? (
                    <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" aria-hidden="true" /> Queuing…</>
                  ) : (
                    <><ScanLine className="w-3 h-3 mr-1.5" aria-hidden="true" /> Run audits on {selectedIds.size} site{selectedIds.size === 1 ? "" : "s"}</>
                  )}
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto max-h-[480px]">
              <table className="w-full border-collapse text-sm" data-testid="geo-leads-table">
                <caption className="sr-only">Discovered businesses and audit status. Use the header checkbox to select all visible eligible rows.</caption>
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="border-b-2 border-zinc-950">
                    <th scope="col" className="px-3 py-2 w-10">
                      <Checkbox
                        checked={allVisibleEligibleSelected ? true : (someVisibleSelected ? "indeterminate" : false)}
                        onCheckedChange={toggleSelectAll}
                        aria-label={allVisibleEligibleSelected ? "Deselect all visible eligible leads" : `Select all ${visibleSelectableIds.length} visible eligible leads`}
                        disabled={visibleSelectableIds.length === 0}
                        className="rounded-none border-zinc-950 data-[state=checked]:bg-zinc-950 data-[state=checked]:text-white"
                        data-testid="geo-select-all-checkbox"
                      />
                    </th>
                    <th scope="col" className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider">Business</th>
                    <th scope="col" className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider">Type</th>
                    <th scope="col" className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider">Dist</th>
                    <th scope="col" className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider">Phone</th>
                    <th scope="col" className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider">Site</th>
                    <th scope="col" className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider">Overlay</th>
                    <th scope="col" className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider">Audit</th>
                    <th scope="col" className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap">Scanned</th>
                    <th scope="col" className="text-right px-3 py-2 text-[10px] font-bold uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.length === 0 && (
                    <tr><td colSpan={10} className="text-center py-12 text-zinc-500 text-sm">
                      Run a Geo-Blitz to populate this list.
                    </td></tr>
                  )}
                  {visibleLeads.map((l, i) => {
                    const checked = selectedIds.has(l.id);
                    const isLocked = !l.website_url || l.audit_status === "QUEUED" || l.audit_status === "RUNNING" || l.audit_status === "COMPLETED";
                    return (
                      <tr key={l.id} onClick={() => setFocusLeadId(l.id)} className={`border-t border-zinc-200 hover:bg-zinc-50 row-anim cursor-pointer ${checked ? "bg-blue-50/60" : ""} ${focusLeadId === l.id ? "ring-1 ring-blue-600 ring-inset" : ""}`} style={{ animationDelay: `${i * 8}ms` }} data-testid={`lead-row-${l.id}`}>
                        <td className="px-3 py-2 align-middle" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={checked}
                            disabled={isLocked}
                            onCheckedChange={() => toggleSelect(l.id)}
                            aria-label={`Select ${l.business_name}`}
                            className="rounded-none border-zinc-950 data-[state=checked]:bg-zinc-950 data-[state=checked]:text-white"
                            data-testid={`lead-select-${l.id}`}
                          />
                        </td>
                        <td className="px-3 py-2 font-semibold">{l.business_name}</td>
                        <td className="px-3 py-2 text-xs uppercase tracking-wide text-zinc-500">{l.business_category || "—"}</td>
                        <td className="px-3 py-2 font-mono text-xs">{l.distance_miles != null ? `${l.distance_miles} mi` : "—"}</td>
                        <td className="px-3 py-2 font-mono text-xs">
                          {l.phone_number ? <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{l.phone_number}</span> : "—"}
                        </td>
                        <td className="px-3 py-2">
                          {l.website_url ? (
                            <a href={l.website_url} target="_blank" rel="noreferrer" className="text-blue-700 text-xs inline-flex items-center gap-1 hover:underline">
                              {new URL(l.website_url).host.replace("www.","")} <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : "—"}
                        </td>
                        <td className="px-3 py-2" data-testid={`lead-overlay-${l.id}`}>
                          {(() => {
                            const status = l.overlay_probe_status || (l.overlay_probed_at == null ? "pending" : (l.overlay_vendor ? "detected" : "not_detected"));
                            if (status === "pending") return <span className="text-[10px] font-mono text-zinc-400">probing…</span>;
                            if (status === "failed") return <span className="text-[10px] font-mono text-red-600" title={l.overlay_probe_error || "Overlay probe failed"}>probe failed</span>;
                            if (status === "detected" && l.overlay_vendor) return (
                              <span
                                title="Verified overlay-widget detection — high-value pitch"
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber-400 text-zinc-950 border border-amber-500"
                              >
                                {l.overlay_vendor}
                              </span>
                            );
                            return <span className="text-[10px] font-mono text-zinc-400">no overlay</span>;
                          })()}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${statusBadge(l.audit_status)}`}>
                            {l.audit_status}
                          </span>
                          {(l.audit_status === "FAILED" || l.audit_status === "SKIPPED") && l.audit_error && (
                            <div className="text-[10px] text-red-700 mt-0.5 max-w-[200px] truncate" title={l.audit_error}>{l.audit_error}</div>
                          )}
                          {l.audit_scan_outcome && l.audit_scan_outcome !== "SUCCESS" && (
                            <div className="text-[10px] text-zinc-500 mt-0.5">outcome: {l.audit_scan_outcome}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-[10px] text-zinc-500 whitespace-nowrap" data-testid={`lead-scanned-${l.id}`}>
                          {l.audit_finished_at
                            ? new Date(l.audit_finished_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
                            : l.audit_status === "QUEUED" || l.audit_status === "RUNNING"
                              ? "in flight…"
                              : "—"}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          {l.audit_status === "COMPLETED" && l.audit_id ? (
                            <a
                              href={`/operator/auditor?audit=${l.audit_id}`}
                              className="inline-flex items-center gap-1 px-2 py-1 border border-zinc-950 text-[10px] font-bold uppercase tracking-wider bg-zinc-950 text-white hover:bg-zinc-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              data-testid={`lead-view-findings-${l.id}`}
                              title="Open this audit in Site Auditor"
                              aria-label={`View findings for ${l.business_name}`}
                            >
                              View findings <ExternalLink className="w-3 h-3" aria-hidden="true" />
                            </a>
                          ) : (l.audit_status === "FAILED" || l.audit_status === "SKIPPED") ? (
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  await api.post(`/geo/leads/${l.id}/retry`);
                                  toast.success("Retry queued");
                                  ensurePolling();
                                } catch (e) {
                                  toast.error(e?.response?.data?.detail || "Retry failed");
                                }
                              }}
                              className="inline-flex items-center gap-1 px-2 py-1 border border-red-700 text-[10px] font-bold uppercase tracking-wider text-red-700 hover:bg-red-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                              data-testid={`lead-retry-${l.id}`}
                              aria-label={`Retry audit for ${l.business_name}`}
                            >
                              Retry
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => auditOne(l)}
                              disabled={isLocked || runningRowId === l.id}
                              title={isLocked ? "Already queued or audited" : "Run audit for this site"}
                              className="inline-flex items-center gap-1 px-2 py-1 border border-zinc-950 text-[10px] font-bold uppercase tracking-wider hover:bg-zinc-950 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-zinc-950 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              data-testid={`lead-audit-${l.id}`}
                              aria-label={`Run audit for ${l.business_name}`}
                            >
                              {runningRowId === l.id ? <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" /> : <Play className="w-3 h-3" aria-hidden="true" />}
                              Audit
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
